import React, { useEffect, useState } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import { LightTooltip } from 'components/shared/CustomTooltip/CustomTooltip';
import { HuddleClientService } from 'service/huddle-client.service';
import RoutePaths from 'routes/RoutePaths';
import zeplinColor from 'theme/Color';

type CalendarDeal = {
  dealId: string;
  name: string;
  businessName: string;
  calendarType: string;
  dateLabel: string;
  dealValue: number;
  amount: number;
  stageName: string;
  termStart: string | null;
  termEnd: string | null;
};

type LegendItem = {
  type: string;
  label: string;
  color: string;
};

type CalendarResponse = {
  clientId: string;
  clientName: string;
  deals: CalendarDeal[];
  legend: LegendItem[];
  totalDeals: number;
  yearRange: { min: number; max: number };
};

const fmtShort = (dateStr: string) => dayjs(dateStr).format('MMM YYYY');

const fmtHoverDate = (termStart: string | null, termEnd: string | null): string => {
  if (!termStart) return '';
  const start = dayjs(termStart);
  if (!termEnd) {
    return `${start.format('MMMM D, YYYY')} - Present`;
  }
  const end = dayjs(termEnd);
  if (start.year() === end.year()) {
    return `${start.format('MMMM D')} - ${end.format('MMMM D, YYYY')}`;
  }
  return `${start.format('MMMM D, YYYY')} - ${end.format('MMMM D, YYYY')}`;
};

const getBarSegment = (
  termStart: string | null,
  termEnd: string | null,
  yearMin: number,
  totalMonths: number,
): { left: number; width: number } => {
  if (!termStart) return { left: 0, width: 0 };
  const start = new Date(termStart);
  const startOffset = (start.getFullYear() - yearMin) * 12 + start.getMonth();
  const leftPct = (startOffset / totalMonths) * 100;

  if (termEnd) {
    const end = new Date(termEnd);
    const endOffset = (end.getFullYear() - yearMin) * 12 + end.getMonth();
    const widthMonths = Math.max(endOffset - startOffset + 1, 1);
    return { left: leftPct, width: (widthMonths / totalMonths) * 100 };
  }
  // No termEnd: extend bar to the end of the timeline
  return { left: leftPct, width: ((totalMonths - startOffset) / totalMonths) * 100 };
};

const ClientCalendarTab = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const splitPath = location.pathname?.split('/');
  const clientId = splitPath[3];

  const [data, setData] = useState<CalendarResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCalendar = async () => {
      if (!clientId) return;
      setLoading(true);
      try {
        const res = await HuddleClientService.getClientV2Calendar(clientId);
        setData(res ?? null);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchCalendar();
  }, [clientId]);

  const deals = data?.deals ?? [];
  const legend = data?.legend ?? [];
  const yearMin = data?.yearRange?.min ?? new Date().getFullYear();
  const yearMax = data?.yearRange?.max ?? yearMin;
  const totalMonths = (yearMax - yearMin + 1) * 12;

  const getBarColor = (calendarType: string): string =>
    legend.find((l) => l.type === calendarType)?.color ?? zeplinColor.BasicBlack;

  return (
    <Box
      sx={{
        border: `1px solid ${zeplinColor.Neutral5}`,
        borderRadius: '14px',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      <Typography variant='responsiveHeading3' color={zeplinColor.BasicBlack} textAlign='left'>
        Deals Calendar
      </Typography>

      {loading ? (
        Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant='rounded' height={34} />)
      ) : deals.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            py: '48px',
          }}
        >
          <img src='/assets/no-chart.svg' width='36px' height='36px' alt='no-data' />
          <Typography variant='responsiveHeading3'>No Deals Available</Typography>
          <Typography variant='responsiveBody3' color={zeplinColor.Neutral8}>
            There are no deals to display on the calendar right now.
          </Typography>
        </Box>
      ) : (
        deals.map((deal) => {
          const { left, width } = getBarSegment(deal.termStart, deal.termEnd, yearMin, totalMonths);
          const barColor = getBarColor(deal.calendarType);
          const handleDealClick = () => navigate(`${RoutePaths.Deals}/${deal.dealId}`);

          const startLabel = deal.termStart ? fmtShort(deal.termStart) : '';
          const endLabel = deal.termEnd ? fmtShort(deal.termEnd) : 'NA';
          const barLabel = startLabel ? `${startLabel} - ${endLabel}` : '';
          const hoverDate = fmtHoverDate(deal.termStart, deal.termEnd);

          return (
            <Box
              key={deal.dealId}
              onClick={handleDealClick}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '16px',
                alignItems: 'center',
                overflow: 'visible',
                cursor: 'pointer',
              }}
            >
              <Typography
                variant='body1'
                sx={{ minWidth: '140px', maxWidth: '140px', flexShrink: 0 }}
                noWrap
              >
                {deal.name}
              </Typography>

              {/* Bar track */}
              <Box
                sx={{
                  flex: 1,
                  position: 'relative',
                  height: '34px',
                  borderRadius: '3px',
                  backgroundColor: zeplinColor.Neutral3,
                  overflow: 'visible',
                }}
              >
                {/* Colored segment wrapped in Tooltip */}
                {width > 0 && (
                  <LightTooltip
                    placement='top'
                    style={{
                      backgroundColor: barColor,
                      borderRadius: '10px',
                      padding: '12px',
                      maxWidth: 'none',
                      color: zeplinColor.Neutral1,
                      boxShadow: 'none',
                    }}
                    title={
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Typography
                          sx={{ fontWeight: 500, fontSize: '14px', color: zeplinColor.Neutral1 }}
                        >
                          {deal.name}
                        </Typography>
                        <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                          {hoverDate}
                        </Typography>
                      </Box>
                    }
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: `${left}%`,
                        width: `${width}%`,
                        borderRadius: '3px',
                        backgroundColor: barColor,
                        display: 'flex',
                        alignItems: 'center',
                        px: '6px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                      }}
                    >
                      <Typography variant='responsiveLabel1' color={zeplinColor.Neutral1} noWrap>
                        {barLabel}
                      </Typography>
                    </Box>
                  </LightTooltip>
                )}
              </Box>
            </Box>
          );
        })
      )}

      {/* Legend */}
      {legend.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '24px',
            flexWrap: 'wrap',
            pt: '4px',
            justifyContent: 'center',
          }}
        >
          {legend.map((item) => (
            <Box
              key={item.type}
              sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '6px' }}
            >
              <Box
                sx={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: item.color,
                  flexShrink: 0,
                }}
              />
              <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ClientCalendarTab;
